// src/components/admin/DashboardChart.tsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/components/DashboardChart.module.css";

interface ChartDataPoint {
  date: string;
  count: number;
}

interface DashboardChartProps {
  data: ChartDataPoint[];
}

const DashboardChart: React.FC<DashboardChartProps> = ({ data }) => {
  const [hoveredBar, setHoveredBar] = useState<{
    index: number;
    x: number;
    y: number;
    date: string;
    count: number;
  } | null>(null);



  // Find max value to scale the chart
  const maxCount = Math.max(...data.map((d) => d.count), 5); // Default min max-value to 5 for empty/low data scales

  // SVG parameters
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 30;
  const paddingBottom = 40;

  const width = 800;
  const height = 300;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  const barCount = data.length;
  // Calculate width per bar including spacing
  const gap = 8;
  const totalGapsWidth = gap * (barCount - 1);
  const barWidth = Math.max((chartWidth - totalGapsWidth) / barCount, 4);

  // Generate Y-axis grid labels (5 lines)
  const yTicksCount = 4;
  const yTicks = Array.from({ length: yTicksCount + 1 }, (_, i) => {
    const value = Math.round((maxCount / yTicksCount) * i);
    const y = chartHeight + paddingTop - (chartHeight / yTicksCount) * i;
    return { value, y };
  });

  return (
    <div className={styles.dashboardChartCard}>
      <div className={styles.dashboardChartHeader}>
        <h3 className={styles.dashboardChartTitle}>Pedidos por Día</h3>
      </div>
      <div className={styles.dashboardChartBody}>
        {!data || data.length === 0 ? (
          <div className={styles.chartEmptyState}>
            <FontAwesomeIcon icon={faClipboardList} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
            <p>No hay datos de pedidos para mostrar en este rango de fechas.</p>
          </div>
        ) : (
          <div className={styles.chartSvgWrapper} style={{ position: "relative" }}>
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width="100%"
              height="100%"
              className={styles.chartSvg}
            >
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--elPaket-primary-color, #2563eb)" stopOpacity="0.85" />
                  <stop offset="100%" stopColor="var(--elPaket-primary-color, #2563eb)" stopOpacity="0.3" />
                </linearGradient>
                <linearGradient id="barGradientHover" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--elPaket-primary-color, #2563eb)" stopOpacity="1" />
                  <stop offset="100%" stopColor="var(--elPaket-primary-color, #2563eb)" stopOpacity="0.6" />
                </linearGradient>
              </defs>

              {/* Grid lines (horizontal) */}
              {yTicks.map((tick, i) => (
                <g key={i} className="chart-grid-line-group">
                  <line
                    x1={paddingLeft}
                    y1={tick.y}
                    x2={width - paddingRight}
                    y2={tick.y}
                    stroke="rgba(0, 0, 0, 0.06)"
                    strokeDasharray="4 4"
                  />
                  <text
                    x={paddingLeft - 10}
                    y={tick.y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill="var(--color-text-secondary, #6b7280)"
                    fontFamily="sans-serif"
                  >
                    {tick.value}
                  </text>
                </g>
              ))}

              {/* Bars */}
              {data.map((d, index) => {
                const barHeight = d.count > 0 ? (d.count / maxCount) * chartHeight : 2; // Always show at least 2px line for visual tracking
                const x = paddingLeft + index * (barWidth + gap);
                const y = chartHeight + paddingTop - barHeight;

                // Date formatting for X-axis: only show subset if too many bars
                const dateObj = new Date(d.date + "T00:00:00");
                const dayLabel = dateObj.getDate().toString();
                const monthLabel = dateObj.toLocaleDateString("es-ES", { month: "short" });

                const showLabel =
                  barCount <= 15 ||
                  index === 0 ||
                  index === barCount - 1 ||
                  index % Math.ceil(barCount / 10) === 0;

                return (
                  <g key={index}>
                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={barHeight}
                      rx={Math.min(barWidth / 2, 4)}
                      fill={
                        hoveredBar?.index === index
                          ? "url(#barGradientHover)"
                          : "url(#barGradient)"
                      }
                      className={styles.chartBar}
                      style={{ transition: "all 0.2s ease" }}
                      onMouseEnter={(e) => {
                        const svgNode = e.currentTarget.ownerSVGElement;
                        if (!svgNode) return;
                        const rect = svgNode.getBoundingClientRect();
                        const xPercent = ((x + barWidth / 2) / width) * rect.width;
                        const yPercent = (y / height) * rect.height;

                        setHoveredBar({
                          index,
                          x: xPercent,
                          y: yPercent - 10,
                          date: d.date,
                          count: d.count,
                        });
                      }}
                      onMouseLeave={() => setHoveredBar(null)}
                    />
                    {showLabel && (
                      <text
                        x={x + barWidth / 2}
                        y={height - paddingBottom + 20}
                        textAnchor="middle"
                        fontSize="10"
                        fill="var(--color-text-secondary, #6b7280)"
                        fontFamily="sans-serif"
                      >
                        {`${dayLabel} ${monthLabel}`}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Floating Tooltip */}
            {hoveredBar && (
              <div
                className={styles.chartTooltip}
                style={{
                  left: `${hoveredBar.x}px`,
                  top: `${hoveredBar.y}px`,
                }}
              >
                <div style={{ fontWeight: "bold" }}>
                  {new Date(hoveredBar.date + "T00:00:00").toLocaleDateString("es-ES", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
                <div>Pedidos: {hoveredBar.count}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardChart;
